import { photo, type PhotoKey } from "@/data/photos";

interface PictureProps {
  photoKey: PhotoKey;
  alt: string;
  /** Sizes attribute for responsive selection; defaults to full viewport width. */
  sizes?: string;
  className?: string;
  /** Eager-load (e.g. LCP hero). Everything else lazy-loads. */
  priority?: boolean;
  style?: React.CSSProperties;
}

/**
 * Responsive image: WebP srcset with a JPEG fallback, intrinsic width/height
 * set from the photo registry so the browser reserves space (no layout shift).
 * Non-priority images lazy-load and decode async. The <img> fills its
 * container via object-fit: cover (parent controls aspect/size).
 */
export function Picture({ photoKey, alt, sizes = "100vw", className, priority = false, style }: PictureProps) {
  const p = photo(photoKey);
  // Priority images (the hero LCP) load eagerly; everything else lazy-loads.
  // We intentionally don't set the `fetchpriority` hint — React-DOM 18.3 warns
  // on the camelCase prop, and `loading="eager"` already prioritises the LCP.
  return (
    <picture>
      <source type="image/webp" srcSet={p.webpSrcSet} sizes={sizes} />
      <img
        src={p.fallback}
        alt={alt}
        width={p.width}
        height={p.height}
        loading={priority ? "eager" : "lazy"}
        decoding="async"
        className={className}
        style={{ width: "100%", height: "100%", objectFit: "cover", ...style }}
      />
    </picture>
  );
}
